'use client';

import type { CSSProperties } from 'react';
import { tx, BlockRoot } from '@akademi/site-builder-runtime';

type Localized = string | Record<string, string>;
export interface SolarHowItWorksStep { title: Localized; body: Localized; }
export interface SolarHowItWorksProps {
  eyebrow?: Localized;
  title?: Localized;
  steps?: SolarHowItWorksStep[];
}

const DEFAULT_STEPS: SolarHowItWorksStep[] = [
  { title: 'Tell us about your roof', body: 'Enter a postcode, roof type and the number of panels you are considering.' },
  { title: 'Review the system', body: 'Compare generation, savings and the equipment needed for your design.' },
  { title: 'Get a checked quote', body: '[confirm] Our team checks the design and confirms the next steps with you.' },
];

export function SolarHowItWorks({ eyebrow = 'How it works', title = 'From first estimate to a system you can understand', steps = DEFAULT_STEPS }: SolarHowItWorksProps) {
  return (
    <BlockRoot as="section" style={sectionStyle}>
      <div style={innerStyle}>
        <span style={eyebrowStyle}>{tx(eyebrow)}</span>
        <h2 style={titleStyle}>{tx(title)}</h2>
        <ol style={listStyle}>
          {steps.map((step, index) => (
            <li key={index} style={itemStyle}>
              <span aria-hidden style={numberStyle}>{index + 1}</span>
              <div><h3 style={stepTitleStyle}>{tx(step.title)}</h3><p style={bodyStyle}>{tx(step.body)}</p></div>
            </li>
          ))}
        </ol>
      </div>
    </BlockRoot>
  );
}

const sectionStyle: CSSProperties = { padding: 'clamp(40px, 7vw, 88px) var(--space-6, 24px)', background: 'var(--color-surface-subtle, #f8fafc)' };
const innerStyle: CSSProperties = { maxWidth: 1180, margin: '0 auto' };
const eyebrowStyle: CSSProperties = { color: 'var(--color-brand-700, #1d4ed8)', fontSize: 'var(--font-size-sm, 14px)', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' };
const titleStyle: CSSProperties = { margin: '10px 0 32px', maxWidth: 680, fontSize: 'clamp(22px, 2.2vw, 28px)', lineHeight: 1.1, fontFamily: 'var(--font-family-display, inherit)' };
const listStyle: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, listStyle: 'none', margin: 0, padding: 0, counterReset: 'steps' };
const itemStyle: CSSProperties = { display: 'flex', gap: 16, alignItems: 'flex-start' };
const numberStyle: CSSProperties = { display: 'grid', placeItems: 'center', width: 36, height: 36, flex: '0 0 36px', borderRadius: '50%', background: 'var(--color-brand-600, #1d4ed8)', color: 'var(--color-text-on-brand, #fff)', fontWeight: 800 };
const stepTitleStyle: CSSProperties = { margin: '2px 0 8px', fontSize: 'var(--font-size-lg, 18px)' };
const bodyStyle: CSSProperties = { margin: 0, color: 'var(--color-text-secondary, #475569)', lineHeight: 1.6 };
